import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import FormField from "@/components/ui/FormField";
import { FileUpload } from "@/components/ui/FileUpload";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/Select";
import { cn } from "@/utils/cn";
import { ApplicationFormValues } from "@/pages/landing/application/applicationFormSchema";
import { COUNTRIES } from "@/data/countries";
import {
  NIGERIA_STATE_LGAS,
  NIGERIA_STATES,
} from "@/data/nigeriaLocations";

interface ContactInformationFormProps {
  onIdentificationSelect: (files: FileList | null) => void;
  identificationFileName?: string;
  identificationError?: string;
  existingIdentificationUrl?: string;
  existingIdentificationName?: string;
}

const ContactInformationForm: React.FC<ContactInformationFormProps> = ({
  onIdentificationSelect,
  identificationFileName,
  identificationError,
  existingIdentificationUrl,
  existingIdentificationName,
}) => {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ApplicationFormValues>();

  const personalErrors = errors.personal ?? {};
  const countryOptions = useMemo(
    () => [...COUNTRIES].sort((a, b) => a.localeCompare(b)),
    [],
  );

  const countryValue = watch("personal.country");
  const stateValue = watch("personal.state");
  const lgaValue = watch("personal.lga");

  const isNigeria = countryValue === "Nigeria";

  const stateOptions = useMemo(() => {
    if (!countryValue) {
      return [];
    }
    return isNigeria ? NIGERIA_STATES : ["Other"];
  }, [countryValue, isNigeria]);

  const lgaOptions = useMemo(() => {
    if (!countryValue) {
      return [];
    }

    if (!isNigeria) {
      return ["Not Applicable"];
    }

    const options = NIGERIA_STATE_LGAS[stateValue ?? ""] ?? [];
    return options.length > 0 ? options : [];
  }, [isNigeria, stateValue]);

  useEffect(() => {
    if (!countryValue) {
      return;
    }

    if (!isNigeria) {
      if (stateValue !== "Other") {
        setValue("personal.state", "Other", { shouldValidate: false });
      }
      if (lgaValue !== "Not Applicable") {
        setValue("personal.lga", "Not Applicable", { shouldValidate: false });
      }
      return;
    }

    if (isNigeria && stateValue && !NIGERIA_STATES.includes(stateValue)) {
      setValue("personal.state", "", { shouldValidate: false });
    }

    const availableLGAs = NIGERIA_STATE_LGAS[stateValue ?? ""] ?? [];
    if (availableLGAs.length === 0) {
      if (lgaValue) {
        setValue("personal.lga", "", { shouldValidate: false });
      }
      return;
    }

    if (!availableLGAs.includes(lgaValue ?? "")) {
      setValue("personal.lga", "", { shouldValidate: false });
    }
  }, [countryValue, isNigeria, lgaValue, setValue, stateValue]);

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <FormField label="Home Address" required className="md:col-span-2">
        <textarea
          placeholder="Enter address"
          className={cn(
            "w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm focus:ring-[#7b2a40] focus:border-[#7b2a40]",
            personalErrors.address &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
          rows={4}
          {...register("personal.address")}
        />
        {personalErrors.address && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.address.message as string}
          </p>
        )}
      </FormField>

      <FormField label="Country" required>
        <Select
          defaultValue=""
          {...register("personal.country")}
          className={cn(
            personalErrors.country &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        >
          <option value="" disabled>
            Select country
          </option>
          {countryOptions.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </Select>
        {personalErrors.country && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.country.message as string}
          </p>
        )}
      </FormField>

      <FormField label="State / Province" required>
        <Select
          defaultValue=""
          disabled={stateOptions.length === 0}
          {...register("personal.state")}
          className={cn(
            personalErrors.state &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        >
          <option value="" disabled>
            {stateOptions.length === 0
              ? "Select country first"
              : isNigeria
                ? "Select state"
                : "Not Applicable"}
          </option>
          {stateOptions.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </Select>
        {personalErrors.state && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.state.message as string}
          </p>
        )}
      </FormField>

      <FormField label="Local Government Area" required>
        <Select
          defaultValue=""
          disabled={lgaOptions.length === 0}
          {...register("personal.lga")}
          className={cn(
            personalErrors.lga &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        >
          <option value="" disabled>
            {countryValue
              ? isNigeria
                ? stateValue
                  ? "Select LGA"
                  : "Select state first"
                : "Not Applicable"
              : "Select country first"}
          </option>
          {lgaOptions.map((lga) => (
            <option key={lga} value={lga}>
              {lga}
            </option>
          ))}
        </Select>
        {personalErrors.lga && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.lga.message as string}
          </p>
        )}
      </FormField>

      <FormField label="City" required>
        <Input
          placeholder="Kaduna"
          {...register("personal.city")}
          className={cn(
            personalErrors.city &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        />
        {personalErrors.city && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.city.message as string}
          </p>
        )}
      </FormField>

      <div className="md:col-span-2">
        <FileUpload
          label="Upload Means of Identification"
          helperText={
            <div className="space-y-1">
              <p>
                Your means of identification must be approved by your country.
                Upload in JPG, JPEG, PNG, PDF, DOC, DOCX format.
              </p>
              {identificationFileName && (
                <p className="text-xs text-slate-500">
                  Selected: {identificationFileName}
                </p>
              )}
              {identificationError && (
                <p className="text-xs text-red-600">{identificationError}</p>
              )}
              {existingIdentificationUrl && (
                <p className="text-xs text-slate-500">
                  Previously uploaded:{" "}
                  <a
                    href={existingIdentificationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#7b2a40] underline"
                  >
                    {existingIdentificationName ?? "View document"}
                  </a>
                </p>
              )}
            </div>
          }
          accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
          onFileSelect={onIdentificationSelect}
        />
      </div>
    </div>
  );
};

export default ContactInformationForm;
